import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Stack,
  Chip
} from '@mui/material';
import { Tab as TabIcon } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { getSessionShareUrl } from '../../utils/shareUrl';

const formatDate = (date) =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

const SessionCard = ({ session, ownerUsername }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [hovered, setHovered] = useState(false);

  const href = getSessionShareUrl(session, ownerUsername);

  const showPreview = isDesktop && hovered && session.topDomains?.length > 0;

  return (
    <Card
      sx={{
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4, transform: 'none' }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CardActionArea component={Link} to={href} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          {/* Color bar */}
          <Box
            sx={{
              width: 6,
              alignSelf: 'stretch',
              minHeight: 40,
              borderRadius: 1,
              bgcolor: session.color || '#1976d2',
              flexShrink: 0
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Name */}
            <Typography variant="body1" fontWeight={600} noWrap sx={{ mb: 0.25 }}>
              {session.name}
            </Typography>

            {/* Description — 2-line clamp */}
            {session.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0.75,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {session.description}
              </Typography>
            )}

            {/* Metadata: tab count + domains + date */}
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              <Chip
                icon={<TabIcon sx={{ fontSize: '13px !important' }} />}
                label={`${session.tabCount} tab${session.tabCount !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
              {session.domains?.length > 0 && (
                <Chip
                  label={
                    session.domains.slice(0, 3).join(', ') +
                    (session.domains.length > 3 ? ` +${session.domains.length - 3}` : '')
                  }
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'action.hover' }}
                />
              )}
              {session.updatedAt && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ lineHeight: '22px', ml: 0.5 }}
                >
                  {formatDate(session.updatedAt)}
                </Typography>
              )}
            </Stack>

            {/* Hover preview — desktop only, topDomains with favicons */}
            <Box
              sx={{
                maxHeight: showPreview ? 200 : 0,
                opacity: showPreview ? 1 : 0,
                overflow: 'hidden',
                transition: 'max-height 0.25s ease, opacity 0.2s ease',
                mt: showPreview ? 1 : 0
              }}
            >
              {session.topDomains?.length > 0 && (
                <Stack spacing={0.5}>
                  {session.topDomains.slice(0, 5).map((d) => (
                    <Stack key={d.hostname} direction="row" alignItems="center" spacing={1}>
                      <Box
                        component="img"
                        src={d.favicon || `https://www.google.com/s2/favicons?domain=${d.hostname}&sz=32`}
                        alt=""
                        sx={{ width: 16, height: 16, borderRadius: 0.5, flexShrink: 0 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                        {d.hostname}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        ({d.count})
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default SessionCard;
